import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { assertNever, capitalize } from '@dhl/utils'
import { type PieceTimeIndication } from '../types/PieceTimeIndication.js'

const dayFormat: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'Europe/Amsterdam',
}

const timeFormat: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Amsterdam',
}

const fullDayFormat: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Amsterdam',
}

export const useTimeIndicationDescription = (): ((
  timeIndication?: PieceTimeIndication
) => string | null | undefined) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const locale = language.replace('_', '-')

  return useCallback(
    (timeIndication?: PieceTimeIndication) => {
      if (!timeIndication) {
        return null
      }

      switch (timeIndication.indicationType) {
        case 'IntervalIndication': {
          const startDate = new Date(timeIndication.start)
          const endDate = new Date(timeIndication.end)
          const periodStart = startDate.toLocaleTimeString(locale, timeFormat)
          const periodEnd = endDate.toLocaleTimeString(locale, timeFormat)

          return t('pieces.shipmentDetail.timeIndicationInterval', {
            day: capitalize(startDate.toLocaleDateString(locale, dayFormat)),
            periodStart,
            periodEnd,
          })
        }

        case 'MomentIndication': {
          const momentDate = new Date(timeIndication.moment)

          return t('pieces.shipmentDetail.timeIndicationMoment', {
            day: capitalize(momentDate.toLocaleDateString(locale, fullDayFormat)),
          })
        }

        default:
          return assertNever(timeIndication)
      }
    },
    [locale, t]
  )
}
